/**
 * VIVA HUB User Profile app — shared account settings for all apps
 */

import React, { useEffect, useMemo, useState } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { useSchedulerAuth } from '../contexts/SchedulerAuthContext'
import { useToast } from '../contexts/ToastContext'
import { usersAPI } from '../utils/scheduler/api'
import HubLayout from '../components/hub/HubLayout'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'

const NOTIFICATION_PREFS = new Set(['none', '24h', '36h', '48h', '72h'])

const selectClassName = cn(
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
  'disabled:cursor-not-allowed disabled:opacity-50'
)

function normalizeNotificationPreference(value) {
  return NOTIFICATION_PREFS.has(value) ? value : '36h'
}

function passwordValidationMessage(password, passwordConfirm) {
  if (!password && !passwordConfirm) return null
  if (password.length > 0 && password.length < 6) {
    return 'Password must be at least 6 characters.'
  }
  if (password !== passwordConfirm) {
    return 'Passwords do not match.'
  }
  if (password.length >= 6) {
    return 'Passwords match.'
  }
  return null
}

export default function HubProfile() {
  const { user, isAdmin, refreshUser } = useSchedulerAuth()
  const { success: toastSuccess, error: toastError } = useToast()
  const [saving, setSaving] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false)
  const [form, setForm] = useState({
    email: '',
    notification_preference: '36h',
    admin_email_notifications: true,
    password: '',
    password_confirm: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    phone: '',
  })

  useEffect(() => {
    if (!user) return
    setForm((prev) => ({
      ...prev,
      email: user.email || '',
      notification_preference: normalizeNotificationPreference(user.notification_preference),
      admin_email_notifications: user.admin_email_notifications !== false,
      address: user.address || '',
      city: user.city || '',
      state: user.state || '',
      zip_code: user.zip_code || '',
      phone: user.phone || '',
    }))
  }, [
    user?.id,
    user?.email,
    user?.notification_preference,
    user?.admin_email_notifications,
    user?.address,
    user?.city,
    user?.state,
    user?.zip_code,
    user?.phone,
  ])

  const passwordMessage = useMemo(
    () => passwordValidationMessage(form.password, form.password_confirm),
    [form.password, form.password_confirm]
  )

  const passwordOk =
    !form.password && !form.password_confirm
      ? true
      : form.password.length >= 6 && form.password === form.password_confirm

  const canSave = Boolean(form.email?.trim()) && passwordOk

  const submit = async (e) => {
    e.preventDefault()

    if (!form.email?.trim()) {
      toastError('Email is required.')
      return
    }
    if (!passwordOk) {
      toastError(passwordMessage || 'Please fix the password fields before saving.')
      return
    }

    const payload = {
      email: form.email.trim(),
      notification_preference: normalizeNotificationPreference(form.notification_preference),
      address: form.address.trim() || null,
      city: form.city.trim() || null,
      state: form.state.trim() || null,
      zip_code: form.zip_code.trim() || null,
      phone: form.phone.trim() || null,
    }
    const trimmedPassword = form.password.trim()
    if (trimmedPassword) payload.password = trimmedPassword
    if (isAdmin) payload.admin_email_notifications = form.admin_email_notifications

    try {
      setSaving(true)
      await usersAPI.updateMe(payload)
      await refreshUser()
      setForm((prev) => ({ ...prev, password: '', password_confirm: '' }))
      toastSuccess(
        trimmedPassword
          ? 'Profile updated. Your new password is active — use it the next time you sign in.'
          : 'Profile updated.'
      )
    } catch (err) {
      let errorMessage = err?.message || 'Failed to update profile.'
      if (err?.data?.details?.length) {
        errorMessage = `Validation error: ${err.data.details
          .map((d) => `${(d.path || []).join('.')}: ${d.message}`)
          .join(', ')}`
      }
      toastError(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  return (
    <HubLayout appName="Profile">
      <Card>
        <CardHeader>
          <CardTitle>Your profile</CardTitle>
          <CardDescription>
            Manage your account settings shared across all VIVA HUB apps.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-6" noValidate>
            <div className="space-y-2">
              <Label htmlFor="hub-profile-email">Email</Label>
              <Input
                id="hub-profile-email"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="hub-profile-notification">Notification preference</Label>
              <select
                id="hub-profile-notification"
                value={form.notification_preference}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, notification_preference: e.target.value }))
                }
                className={selectClassName}
              >
                <option value="none">None</option>
                <option value="24h">24 hours</option>
                <option value="36h">36 hours</option>
                <option value="48h">48 hours</option>
                <option value="72h">72 hours</option>
              </select>
              <p className="text-xs text-muted-foreground">
                Applies to Room Scheduler reservation reminders.
              </p>
            </div>

            {isAdmin ? (
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.admin_email_notifications}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      admin_email_notifications: e.target.checked,
                    }))
                  }
                  className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
                />
                <span className="text-sm text-foreground">Receive admin email notifications</span>
              </label>
            ) : null}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {[
                ['phone', 'Phone', 'tel'],
                ['address', 'Address', 'text'],
                ['city', 'City', 'text'],
                ['state', 'State', 'text'],
                ['zip_code', 'Zip code', 'text'],
              ].map(([key, label, type]) => (
                <div key={key} className="space-y-2">
                  <Label htmlFor={`hub-profile-${key}`}>{label}</Label>
                  <Input
                    id={`hub-profile-${key}`}
                    type={type}
                    value={form[key]}
                    onChange={(e) => setForm((prev) => ({ ...prev, [key]: e.target.value }))}
                  />
                </div>
              ))}
            </div>

            <Separator />

            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-medium text-foreground">Change password</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Leave blank to keep your current password.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="hub-profile-password">New password</Label>
                  <div className="relative">
                    <Input
                      id="hub-profile-password"
                      type={showPassword ? 'text' : 'password'}
                      value={form.password}
                      onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))}
                      className="pr-11"
                      autoComplete="new-password"
                      minLength={6}
                      placeholder="At least 6 characters"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute inset-y-0 right-0 h-full w-10 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowPassword((visible) => !visible)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      aria-pressed={showPassword}
                      aria-controls="hub-profile-password"
                    >
                      {showPassword ? <EyeOff /> : <Eye />}
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="hub-profile-password-confirm">Confirm password</Label>
                  <div className="relative">
                    <Input
                      id="hub-profile-password-confirm"
                      type={showPasswordConfirm ? 'text' : 'password'}
                      value={form.password_confirm}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, password_confirm: e.target.value }))
                      }
                      className="pr-11"
                      autoComplete="new-password"
                      minLength={6}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute inset-y-0 right-0 h-full w-10 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowPasswordConfirm((visible) => !visible)}
                      aria-label={
                        showPasswordConfirm
                          ? 'Hide password confirmation'
                          : 'Show password confirmation'
                      }
                      aria-pressed={showPasswordConfirm}
                      aria-controls="hub-profile-password-confirm"
                    >
                      {showPasswordConfirm ? <EyeOff /> : <Eye />}
                    </Button>
                  </div>
                </div>
              </div>
              {passwordMessage ? (
                <p
                  className={cn(
                    'text-sm',
                    passwordOk && (form.password || form.password_confirm)
                      ? 'text-emerald-700'
                      : 'text-destructive'
                  )}
                >
                  {passwordMessage}
                </p>
              ) : null}
            </div>

            <div className="flex justify-end">
              <Button type="submit" variant="success" disabled={!canSave || saving}>
                {saving ? 'Saving...' : 'Save profile'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </HubLayout>
  )
}
